
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  Calendar, AlertTriangle, Clock, CreditCard, 
  AlertCircle, CheckCircle, Info, CalendarDays, ExternalLink
} from 'lucide-react';
import { format, isToday, isTomorrow, formatDistance } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useUpcomingSubscriptionPayments } from '../hooks/useUpcomingSubscriptionPayments';
import { Badge } from '@/components/ui/badge';
import { useTheme } from '@/contexts/ThemeContext';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';

export const UpcomingSubscriptionPaymentCard: React.FC = () => {
  const { upcomingPayments, loading, error, hasUpcomingPayments } = useUpcomingSubscriptionPayments();
  const { paymentMethod, loading: paymentLoading } = usePaymentMethods();
  const { theme } = useTheme();
  
  if (loading) {
    return (
      <Card className="shadow-sm overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-blue-50 opacity-50"></div>
        <CardHeader>
          <Skeleton className="h-6 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full mb-2" />
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Ошибка</AlertTitle>
        <AlertDescription>
          Не удалось загрузить информацию о предстоящих платежах. Пожалуйста, попробуйте позже.
        </AlertDescription>
      </Alert>
    );
  }
  
  if (!hasUpcomingPayments) {
    return null; // Не показываем карточку, если нет предстоящих платежей
  }
  
  const payment = upcomingPayments[0]; // Берем ближайший платеж
  const paymentDate = new Date(payment.next_payment_date);
  
  const formatPaymentDate = () => {
    if (isToday(paymentDate)) {
      return 'сегодня';
    }
    if (isTomorrow(paymentDate)) {
      return 'завтра';
    }
    return formatDistance(paymentDate, new Date(), { locale: ru, addSuffix: true });
  };
  
  // Получение данных для стилей на основе дней до платежа
  const getCardStyles = () => {
    if (payment.days_until_payment <= 0) {
      return {
        gradientClass: 'from-red-500 to-orange-400',
        progressColor: 'bg-red-500',
        progressValue: 100,
        icon: <AlertCircle className="h-5 w-5 text-red-100" />
      };
    }
    if (payment.days_until_payment <= 2) {
      return {
        gradientClass: 'from-orange-500 to-yellow-400',
        progressColor: 'bg-orange-500',
        progressValue: 100 - (payment.days_until_payment * 30),
        icon: <Info className="h-5 w-5 text-orange-100" />
      };
    }
    return {
      gradientClass: 'from-blue-500 to-cyan-400',
      progressColor: 'bg-blue-500',
      progressValue: Math.max(5, 100 - (payment.days_until_payment * 15)),
      icon: <CalendarDays className="h-5 w-5 text-blue-100" />
    };
  };
  
  const { gradientClass, progressColor, progressValue, icon } = getCardStyles();
  
  const formatInterval = (interval: string) => {
    switch (interval) {
      case 'day': return 'ежедневный';
      case 'week': return 'еженедельный';
      case 'month': return 'ежемесячный';
      case 'year': return 'ежегодный';
      default: return interval;
    }
  };

  // Получение иконки для типа карты
  const getCardIcon = () => {
    if (paymentLoading || !paymentMethod) return null;
    
    const cardType = paymentMethod.cardType?.toLowerCase();
    
    if (!cardType) return <CreditCard className="h-5 w-5" />;
    
    if (cardType.includes('visa')) {
      return <CreditCard className="h-5 w-5 text-blue-500" />; // Visa
    } else if (cardType.includes('master')) {
      return <CreditCard className="h-5 w-5 text-orange-500" />; // MasterCard
    } else if (cardType.includes('mir')) {
      return <CreditCard className="h-5 w-5 text-green-500" />; // МИР
    }
    
    return <CreditCard className="h-5 w-5" />; // Другой тип карты
  };
  
  // Формирование маскированного номера карты
  const getMaskedCardNumber = () => {
    if (!paymentMethod || !paymentMethod.cardLast4) {
      return 'Карта не привязана';
    }
    return `**** **** **** ${paymentMethod.cardLast4}`;
  };
  
  // Формирование информации о сроке действия карты
  const getCardExpiry = () => {
    if (!paymentMethod || !paymentMethod.expiryMonth || !paymentMethod.expiryYear) {
      return null;
    }
    return `${paymentMethod.expiryMonth}/${paymentMethod.expiryYear}`;
  };
  
  // Получение сокращенного названия типа карты
  const getCardTypeName = () => {
    if (!paymentMethod || !paymentMethod.cardType) return '';
    
    const cardType = paymentMethod.cardType.toLowerCase();
    
    if (cardType.includes('visa')) {
      return 'Visa';
    } else if (cardType.includes('master')) {
      return 'MasterCard';
    } else if (cardType.includes('mir')) {
      return 'МИР';
    }
    
    return paymentMethod.cardType;
  };

  // Формирование URL для Telegram с предзаполненным текстом запроса
  const getTelegramUrl = () => {
    return 'https://t.me/tuk_tuk_rent?start=change_payment';
  };

  return (
    <TooltipProvider>
      <Card className={`shadow-md overflow-hidden relative animate-fade-in rounded-xl border-0`}>
        {/* Градиентный фон */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} opacity-80 
          ${theme === 'dark' ? 'opacity-30' : 'opacity-80'}`}>
        </div>
        
        {/* Верхнее поле карточки */}
        <CardHeader className="pb-2 relative z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              {icon}
              <CardTitle className={`text-lg ml-2 ${theme === 'dark' ? 'text-white' : 'text-white'}`}>
                Скоро спишется платёж
              </CardTitle>
            </div>
            <Badge variant="outline" className={`capitalize ${theme === 'dark' ? 'bg-white/10 text-white' : 'bg-white/70 text-gray-800'}`}>
              {formatInterval(payment.interval)}
            </Badge>
          </div>
          <CardDescription className={theme === 'dark' ? 'text-gray-300' : 'text-white/90'}>
            По подписке {payment.human_number || `№${payment.subscription_id.slice(0, 8)}`}
          </CardDescription>
        </CardHeader>
        
        {/* Основной контент */}
        <CardContent className="relative z-10">
          <div className="flex items-center justify-between mb-4 mt-2">
            <div className="flex items-center">
              <Calendar className={`h-5 w-5 mr-2 ${theme === 'dark' ? 'text-gray-300' : 'text-white/90'}`} />
              <span className={theme === 'dark' ? 'text-white' : 'text-white font-medium'}>
                {format(paymentDate, 'd MMMM yyyy', { locale: ru })}
              </span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-sm ${theme === 'dark' ? 'bg-white/10 text-white' : 'bg-white/30 text-gray-800'}`}>
                {formatPaymentDate()}
              </span>
            </div>
            <div className={`font-bold text-xl ${theme === 'dark' ? 'text-white' : 'text-white'}`}>
              {payment.amount.toLocaleString('ru-RU')} ₽
            </div>
          </div>
          
          {/* Прогресс-бар */}
          <div className="mb-2">
            <div className="flex justify-between text-xs mb-1">
              <span className={theme === 'dark' ? 'text-gray-300' : 'text-white/90'}>До списания:</span>
              <span className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-white'}`}>
                {payment.days_until_payment === 0 
                  ? 'Сегодня!' 
                  : payment.days_until_payment === 1 
                    ? 'Завтра' 
                    : `${payment.days_until_payment} дн.`}
              </span>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Progress 
                  value={progressValue} 
                  className="h-2 bg-white/30" 
                  indicatorClassName={progressColor}
                />
              </TooltipTrigger>
              <TooltipContent>
                {progressValue >= 90 
                  ? 'Платеж очень скоро!' 
                  : progressValue >= 50 
                    ? 'Платеж приближается' 
                    : 'Есть время подготовиться'}
              </TooltipContent>
            </Tooltip>
          </div>
        </CardContent>
        
        {/* Нижнее поле - информация о карте */}
        <CardFooter className={`relative z-10 ${theme === 'dark' ? 'bg-black/20' : 'bg-white/10'} pt-3 pb-3 flex flex-col sm:flex-row gap-3`}>
          {paymentLoading ? (
            <div className="w-full flex items-center">
              <Skeleton className="h-6 w-24 mr-2" />
              <Skeleton className="h-6 w-32" />
            </div>
          ) : paymentMethod && paymentMethod.paymentMethodId ? (
            <>
              <div className="flex items-center text-sm text-white/80 flex-grow">
                <div className="flex items-center space-x-2">
                  {getCardIcon()}
                  <div className="flex flex-col">
                    <span className="flex items-center">
                      <span className="font-medium">{getMaskedCardNumber()}</span>
                      {getCardExpiry() && (
                        <span className="text-xs ml-2 opacity-80">до {getCardExpiry()}</span>
                      )}
                    </span>
                    <span className="text-xs opacity-70">{getCardTypeName()}</span>
                  </div>
                </div>
              </div>
              <a 
                href={getTelegramUrl()}
                target="_blank"
                rel="noopener noreferrer" 
                className={`flex items-center text-sm px-3 py-1.5 rounded-md 
                  ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : 'bg-white/20 hover:bg-white/30'} 
                  transition-colors text-white`}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Изменить способ оплаты
              </a>
            </>
          ) : (
            <>
              <div className="flex items-center text-sm text-white/80 flex-grow">
                <AlertTriangle className="h-4 w-4 mr-2 text-yellow-300" />
                <span>Способ оплаты не привязан</span>
              </div>
              <a 
                href={getTelegramUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center text-sm px-3 py-1.5 rounded-md font-medium
                  ${theme === 'dark' ? 'bg-white/90 text-gray-800' : 'bg-white/90 text-gray-800'} 
                  hover:brightness-105 transition-all`}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Связаться с поддержкой
              </a>
            </>
          )}
        </CardFooter>
      </Card>
    </TooltipProvider>
  );
};

